import axios from 'axios';
import api from '../../../utils/api';

const API_URL = import.meta.env.VITE_API_URL 
  ? `${import.meta.env.VITE_API_URL}/transactions`
  : 'http://localhost:5000/api/transactions';

console.log('🔧 Transaction Service API URL:', API_URL);

// Helper function to safely extract transaction data from API responses
const extractTransactionData = (response) => {
  if (!response || !response.data) {
    console.warn('Invalid response format:', response);
    return {
      transactions: [],
      pagination: {
        total: 0,
        page: 1,
        limit: 50,
        pages: 0
      }
    };
  }
  
  // Check if data is directly in the response or nested
  const data = response.data;
  
  if (data.transactions) {
    return {
      transactions: data.transactions,
      pagination: data.pagination || {
        total: data.transactions.length,
        page: 1,
        limit: 50,
        pages: Math.ceil(data.transactions.length / 50)
      }
    };
  }
  
  if (Array.isArray(data)) {
    return {
      transactions: data,
      pagination: {
        total: data.length,
        page: 1,
        limit: 50,
        pages: Math.ceil(data.length / 50)
      }
    };
  }
  
  // Last attempt - check for data.data
  if (data.data) {
    if (Array.isArray(data.data)) {
      return {
        transactions: data.data,
        pagination: {
          total: data.data.length,
          page: 1,
          limit: 50,
          pages: Math.ceil(data.data.length / 50)
        }
      };
    }
    
    if (data.data.transactions) {
      return {
        transactions: data.data.transactions,
        pagination: data.data.pagination || {
          total: data.data.transactions.length,
          page: 1,
          limit: 50,
          pages: Math.ceil(data.data.transactions.length / 50)
        }
      };
    }
  }
  
  console.warn('Could not extract transaction data from response:', response);
  return {
    transactions: [],
    pagination: {
      total: 0,
      page: 1,
      limit: 50,
      pages: 0
    }
  };
};

// Helper function to transform Plaid transactions to match standard transaction format
const transformPlaidTransactions = (plaidTransactions, accountId) => {
  if (!plaidTransactions || !Array.isArray(plaidTransactions)) {
    return [];
  }
  
  return plaidTransactions.map(tx => {
    // Ensure we have valid data
    if (!tx) return null;
    
    // Handle possible structure differences
    const transaction = tx.original || tx;
    
    return {
      _id: tx._id || `plaid_${transaction.plaidTransactionId || transaction.transaction_id}`,
      date: transaction.date,
      description: transaction.name || transaction.description,
      amount: Math.abs(transaction.amount),
      type: transaction.amount < 0 ? 'expense' : 'income',
      wallet: {
        _id: accountId,
        name: transaction.accountName || 'Plaid Account'
      },
      category: {
        _id: transaction.categoryId || 'uncategorized',
        name: transaction.plaidCategory && transaction.plaidCategory.length > 0 
          ? transaction.plaidCategory[0] 
          : (transaction.category && transaction.category.length > 0 ? transaction.category[0] : 'Uncategorized'),
        color: '#808080'
      },
      pending: !!transaction.pending,
      metadata: {
        plaidTransactionId: transaction.plaidTransactionId || transaction.transaction_id,
        source: 'plaid',
        original: transaction,
        institutionName: transaction.institutionName || 'Financial Institution',
        accountMask: transaction.accountMask || transaction.mask
      }
    };
  }).filter(tx => tx !== null); // Remove any nulls from the result
};

const transactionService = {
  /**
   * Get all transactions with pagination
   * @param {Object} options - Options for fetching transactions
   * @returns {Promise<Object>} - Promise resolving to transaction data
   */
  getAllTransactions: async (options = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (options.page) queryParams.append('page', options.page);
      if (options.limit) queryParams.append('limit', options.limit);
      if (options.ownerType) queryParams.append('ownerType', options.ownerType);
      if (options.source) queryParams.append('source', options.source);
      if (options.sortBy) queryParams.append('sortBy', options.sortBy);
      if (options.sortDirection) queryParams.append('sortDirection', options.sortDirection);
      
      const endpoint = `/api/transactions?${queryParams}`;
      console.log(`🔍 Calling API endpoint: ${endpoint}`, options);
      
      const response = await api.get(endpoint);
      
      // Use helper to extract data safely
      const data = extractTransactionData(response);
      console.log(`📊 Transaction count from server: ${data.pagination.total}, page ${data.pagination.page} of ${data.pagination.pages}`);
      
      // We no longer need to fetch Plaid transactions separately since they're included in getCombinedTransactions
      
      return {
        success: true,
        data
      };
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      throw error;
    }
  },

  /**
   * Get transactions for a specific account
   * @param {string} accountId - Account ID
   * @param {Object} options - Options for fetching transactions
   * @returns {Promise<Object>} - Promise resolving to transaction data
   */
  getAccountTransactions: async (accountId, options = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (options.page) queryParams.append('page', options.page);
      if (options.limit) queryParams.append('limit', options.limit);
      if (options.ownerType) queryParams.append('ownerType', options.ownerType);
      if (options.source) queryParams.append('source', options.source);
      if (options.sortBy) queryParams.append('sortBy', options.sortBy);
      if (options.sortDirection) queryParams.append('sortDirection', options.sortDirection);
      
      const endpoint = `/api/transactions/account/${accountId}?${queryParams}`;
      console.log(`🔍 Calling API endpoint: ${endpoint}`, options);
      
      const response = await api.get(endpoint);
      
      // Use helper to extract data safely
      const data = extractTransactionData(response);
      console.log(`📊 Transaction count from server for account ${accountId}: ${data.pagination.total}, page ${data.pagination.page} of ${data.pagination.pages}`);
      
      // We no longer need to fetch Plaid transactions separately since they're included in the server response
      
      return {
        success: true,
        data
      };
    } catch (error) {
      console.error('Failed to fetch account transactions:', error);
      throw error;
    }
  },

  /**
   * Update transaction owner type
   * @param {string} transactionId - Transaction ID
   * @param {string} ownerType - Owner type (personal, partner, joint)
   * @param {Object} ownerDetails - Optional owner details
   * @returns {Promise<Object>} - Promise resolving to updated transaction
   */
  updateTransactionOwner: async (transactionId, ownerType, ownerDetails = null) => {
    try {
      const response = await api.put(`/api/transactions/${transactionId}/owner`, {
        ownerType,
        ...(ownerDetails ? { ownerDetails } : {})
      });
      return response.data;
    } catch (error) {
      console.error('Failed to update transaction owner:', error);
      throw error;
    }
  },

  /**
   * Bulk update transaction owner types
   * @param {Array<string>} transactionIds - Array of transaction IDs
   * @param {string} ownerType - Owner type (personal, partner, joint)
   * @param {Object} ownerDetails - Optional owner details
   * @returns {Promise<Object>} - Promise resolving to update result
   */
  bulkUpdateTransactionOwner: async (transactionIds, ownerType, ownerDetails = null) => {
    try {
      const response = await api.put(`/api/transactions/bulk-update-owner`, {
        transactionIds,
        ownerType,
        ...(ownerDetails ? { ownerDetails } : {})
      });
      return response.data;
    } catch (error) {
      console.error('Failed to bulk update transaction owners:', error);
      throw error;
    }
  },

  /**
   * Get transaction owner type distribution
   * @returns {Promise<Object>} - Promise resolving to owner type distribution data
   */
  getOwnerTypeDistribution: async () => {
    try {
      const response = await api.get('/api/transactions/owner-distribution');
      return response.data;
    } catch (error) {
      console.error('Failed to get owner type distribution:', error);
      throw error;
    }
  },

  /**
   * Fix missing account info in transactions
   * @returns {Promise<Object>} - Promise resolving to fix result
   */
  fixAccountInfo: async () => {
    try {
      console.log('🔧 Fixing transaction account info...');
      const response = await api.get('/api/transactions/fix-accounts');
      console.log('✅ Fix account info response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to fix account info:', error);
      throw error;
    }
  },

  /**
   * Manually trigger Plaid transaction sync for a specific account
   * @param {string} accountId - Plaid account ID to sync
   * @returns {Promise<Object>} - Promise resolving to sync result
   */
  syncPlaidTransactions: async (accountId) => {
    try {
      console.log(`🔄 Triggering Plaid transaction sync for account ${accountId}`);
      const response = await api.post(`/api/plaid/sync-transactions/${accountId}`);
      console.log('✅ Sync response:', response.data);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('❌ Error syncing Plaid transactions:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  },

  /**
   * Sync all Plaid transactions for the user
   * @returns {Promise<Object>} - Promise resolving to sync result
   */
  syncAllPlaidTransactions: async () => {
    try {
      console.log('🔄 Triggering sync for all Plaid accounts');
      
      // First get all Plaid items (connections)
      const itemsResponse = await api.get('/api/plaid/items');
      const items = itemsResponse.data;
      
      if (!items || items.length === 0) {
        console.log('⚠️ No Plaid connections found to sync');
        return {
          success: true,
          message: 'No Plaid connections found to sync',
          data: { syncedCount: 0 }
        };
      }
      
      console.log(`📊 Found ${items.length} Plaid connections to sync`);
      
      // Set date range for last 90 days
      const endDate = new Date().toISOString().slice(0, 10);
      const startDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
      
      // Sync transactions for each Plaid connection
      const results = await Promise.all(
        items.map(item => 
          api.post(`/api/plaid/sync-transactions/${item.itemId}`, {
            createStandardTransactions: true,
            startDate,
            endDate
          })
        )
      );
      
      console.log(`✅ Successfully synced ${results.length} Plaid connections`);
      
      return {
        success: true,
        message: `Synced transactions from ${results.length} bank connection(s)`,
        data: {
          syncedCount: results.length,
          results: results.map(r => r.data)
        }
      };
    } catch (error) {
      console.error('❌ Error syncing all Plaid transactions:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  },

  // Add new transaction
  addTransaction: async (accountId, transactionData) => {
    try {
      console.log(`➕ Adding transaction to account ${accountId}:`, transactionData);
      
      // Use the api utility instead of direct axios call
      const response = await api.post(`/api/transactions/account/${accountId}`, transactionData);
      
      console.log('✅ Transaction added:', response.data);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('❌ Error adding transaction:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  },

  /**
   * Update transaction (category, description, etc.)
   * @param {string} transactionId - Transaction ID
   * @param {Object} updateData - Data to update (category, description)
   * @returns {Promise<Object>} - Promise resolving to updated transaction
   */
  updateTransaction: async (transactionId, updateData) => {
    try {
      console.log(`✏️ Updating transaction ${transactionId}:`, updateData);
      
      // Check if this is a Plaid transaction (ID starts with "plaid_")
      if (transactionId.startsWith('plaid_')) {
        // For Plaid transactions, just update the category in the UI for now
        // In the future, we could implement an API to save user categorizations for Plaid transactions
        console.log('This is a Plaid transaction - UI updates only');
        return {
          success: true,
          message: 'Plaid transaction updated in UI only',
          data: { ...updateData, _id: transactionId }
        };
      }
      
      // For regular transactions, proceed with API call
      const response = await api.put(`/api/transactions/${transactionId}`, updateData);
      console.log('✅ Transaction updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating transaction:', error);
      throw error;
    }
  },

  /**
   * Bulk update categories for multiple transactions
   * @param {Array} transactionIds - Array of transaction IDs to update
   * @param {String} category - Category to assign to all selected transactions
   * @returns {Promise} - API response
   */
  bulkUpdateCategory: async (transactionIds, category) => {
    try {
      // Filter out Plaid transaction IDs
      const standardTransactionIds = transactionIds.filter(id => !id.startsWith('plaid_'));
      
      if (standardTransactionIds.length === 0) {
        console.log('No standard transactions to update');
        return { 
          success: true, 
          message: 'No standard transactions to update', 
          updatedCount: 0 
        };
      }
      
      const response = await api.put('/api/transactions/bulk-update-category', {
        transactionIds: standardTransactionIds,
        category
      });
      
      // Log how many Plaid transactions were skipped
      const plaidTransactionsCount = transactionIds.length - standardTransactionIds.length;
      if (plaidTransactionsCount > 0) {
        console.log(`Note: ${plaidTransactionsCount} Plaid transactions were not updated on the server`);
      }
      
      return {
        ...response.data,
        plaidTransactionsSkipped: plaidTransactionsCount
      };
    } catch (error) {
      console.error('Error in bulk update category:', error);
      throw error;
    }
  },

  /**
   * Bulk delete multiple transactions
   * @param {Array} transactionIds - Array of transaction IDs to delete
   * @returns {Promise} - API response
   */
  bulkDeleteTransactions: async (transactionIds) => {
    try {
      // Filter out Plaid transaction IDs - we don't allow deleting Plaid transactions
      const standardTransactionIds = transactionIds.filter(id => !id.startsWith('plaid_'));
      
      if (standardTransactionIds.length === 0) {
        console.log('No standard transactions to delete');
        return { 
          success: true, 
          message: 'No standard transactions to delete', 
          deletedCount: 0 
        };
      }
      
      const response = await api.post('/api/transactions/bulk-delete', {
        transactionIds: standardTransactionIds
      });
      
      // Log how many Plaid transactions were skipped
      const plaidTransactionsCount = transactionIds.length - standardTransactionIds.length;
      if (plaidTransactionsCount > 0) {
        console.log(`Note: ${plaidTransactionsCount} Plaid transactions cannot be deleted`);
      }
      
      return {
        ...response.data,
        plaidTransactionsSkipped: plaidTransactionsCount
      };
    } catch (error) {
      console.error('Error in bulk delete transactions:', error);
      throw error;
    }
  },

  /**
   * Import multiple transactions
   * @param {Array} transactions - Array of transaction objects to import
   * @returns {Promise} - API response with import results
   */
  importTransactions: async (transactions) => {
    try {
      console.log(`📥 Importing ${transactions.length} transactions`);
      const response = await api.post('/api/transactions/import', {
        transactions
      });
      console.log('✅ Import successful:', response.data);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('❌ Error importing transactions:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  },

  /**
   * Get transaction diagnostics (counts from various sources)
   * @returns {Promise} - Diagnostic data
   */
  getDiagnostics: async () => {
    try {
      console.log('🔍 Fetching transaction diagnostics...');
      const response = await api.get('/api/transactions/diagnostic');
      console.log('📊 Diagnostic data:', response.data);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('❌ Error fetching diagnostics:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  },

  // Add a method to get combined transactions with correct pagination
  getCombinedTransactions: async (options = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (options.page) queryParams.append('page', options.page);
      if (options.limit) queryParams.append('limit', options.limit);
      if (options.ownerType) queryParams.append('ownerType', options.ownerType);
      if (options.sortBy) queryParams.append('sortBy', options.sortBy);
      if (options.sortDirection) queryParams.append('sortDirection', options.sortDirection);
      
      console.log('🔍 Calling combined transactions endpoint with options:', options);
      const response = await api.get(`/api/transactions/combined?${queryParams}`);
      console.log('📊 Combined transactions response:', response.data);
      
      // Use helper to extract data safely
      const data = extractTransactionData(response);
      console.log(`📊 Combined transactions count: ${data.pagination.total}`);
      
      return {
        success: true,
        data
      };
    } catch (error) {
      console.error('Failed to fetch combined transactions:', error);
      throw error;
    }
  },

  /**
   * Get expense statistics
   * @returns {Promise<Object>} - Promise resolving to expense statistics
   */
  getExpenseStatistics: async () => {
    try {
      console.log('🔍 Fetching expense statistics...');
      const response = await api.get('/api/transactions/statistics/expenses');
      console.log('�� Expense statistics API response:', response.data);
      
      // Always calculate from transactions to ensure we have accurate data
      // This works regardless of whether the API endpoint works
      console.log('🧮 Calculating statistics from transactions...');
      
      // Get all transactions - we'll filter for expenses
      const transactionsResponse = await api.get('/api/transactions?limit=500');
      const allTransactions = extractTransactionData(transactionsResponse).transactions || [];
      
      console.log(`📊 Found ${allTransactions.length} total transactions`);
      
      // Filter just for expenses (negative amounts)
      const expenseTransactions = allTransactions.filter(tx => 
        (typeof tx.amount === 'number' && tx.amount < 0) || 
        tx.type === 'expense'
      );
      
      console.log(`📊 Found ${expenseTransactions.length} expense transactions`);
      
      // Calculate statistics
      const currentDate = new Date();
      // Set to start of current day for accurate comparison
      currentDate.setHours(0, 0, 0, 0);
      
      const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      firstDayOfMonth.setHours(0, 0, 0, 0);
      
      const lastDayOfPrevMonth = new Date(firstDayOfMonth);
      lastDayOfPrevMonth.setDate(lastDayOfPrevMonth.getDate() - 1);
      lastDayOfPrevMonth.setHours(23, 59, 59, 999);
      
      const firstDayOfPrevMonth = new Date(lastDayOfPrevMonth.getFullYear(), lastDayOfPrevMonth.getMonth(), 1);
      firstDayOfPrevMonth.setHours(0, 0, 0, 0);
      
      // Filter transactions for current month only (from 1st to today)
      const currentMonthTransactions = expenseTransactions.filter(tx => {
        try {
          const txDate = new Date(tx.date);
          txDate.setHours(0, 0, 0, 0);
          // Only include transactions from the 1st of current month through today
          return txDate >= firstDayOfMonth && txDate <= currentDate;
        } catch (e) {
          console.error('Error parsing date', tx.date);
          return false;
        }
      });
      
      const prevMonthTransactions = expenseTransactions.filter(tx => {
        try {
          const txDate = new Date(tx.date);
          return txDate >= firstDayOfPrevMonth && txDate <= lastDayOfPrevMonth;
        } catch (e) {
          console.error('Error parsing date', tx.date);
          return false;
        }
      });
      
      console.log(`📊 Found ${currentMonthTransactions.length} expenses for current month`);
      console.log(`📊 Found ${prevMonthTransactions.length} expenses for previous month`);
      
      // Calculate total expenses for current month (use absolute value since expenses are negative)
      const totalMonthlyExpenses = currentMonthTransactions.reduce((sum, tx) => {
        return sum + Math.abs(tx.amount || 0);
      }, 0);
      
      // Calculate total expenses for previous month (use absolute value)
      const prevMonthExpenses = prevMonthTransactions.reduce((sum, tx) => {
        return sum + Math.abs(tx.amount || 0);
      }, 0);
      
      // Calculate month over month change
      const monthOverMonthChange = prevMonthExpenses > 0
        ? ((totalMonthlyExpenses - prevMonthExpenses) / prevMonthExpenses) * 100
        : 0;
      
      // Calculate average daily spend
      const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
      const daysPassed = Math.min(currentDate.getDate(), daysInMonth);
      const averageDailySpend = totalMonthlyExpenses / daysPassed;
      
      // Categorize transactions and find largest category
      const categoryTotals = {};
      currentMonthTransactions.forEach(tx => {
        const categoryName = tx.categoryRef?.name || tx.category?.name || 'Uncategorized';
        categoryTotals[categoryName] = (categoryTotals[categoryName] || 0) + Math.abs(tx.amount || 0);
      });
      
      // Find the largest category
      let largestCategory = {
        name: 'Uncategorized',
        amount: 0,
        percentage: 0
      };
      
      Object.entries(categoryTotals).forEach(([name, amount]) => {
        if (amount > largestCategory.amount) {
          largestCategory = {
            name,
            amount,
            percentage: totalMonthlyExpenses > 0 ? (amount / totalMonthlyExpenses) * 100 : 0
          };
        }
      });
      
      // Identify recurring expenses (subscriptions: same merchant + same amount)
      // Group by normalized merchant name AND amount (within $0.50 tolerance for rounding)
      const normalizeDescription = (description) => {
        return (description || '').toLowerCase()
          .replace(/\d+/g, '') // Remove numbers
          .replace(/\s+/g, ' ') // Normalize whitespace
          .trim();
      };
      
      // Round amount to nearest dollar for grouping (handles small variations)
      const roundAmount = (amount) => {
        return Math.round(Math.abs(amount));
      };
      
      // Create a key combining merchant and rounded amount
      const createRecurringKey = (tx) => {
        const merchant = normalizeDescription(tx.description);
        const amount = roundAmount(tx.amount);
        return `${merchant}::${amount}`;
      };
      
      // Group transactions by merchant + amount
      const recurringGroups = {};
      expenseTransactions.forEach(tx => {
        const key = createRecurringKey(tx);
        if (key && key !== '::0') { // Skip empty keys
          if (!recurringGroups[key]) {
            recurringGroups[key] = [];
          }
          recurringGroups[key].push(tx);
        }
      });
      
      // Find recurring subscriptions (same merchant + same amount, appearing 2+ times)
      const recurringKeys = Object.keys(recurringGroups)
        .filter(key => recurringGroups[key].length >= 2);
      
      // Calculate total recurring expenses for current month
      const recurringExpenses = currentMonthTransactions
        .filter(tx => {
          const key = createRecurringKey(tx);
          return recurringKeys.includes(key);
        })
        .reduce((sum, tx) => sum + Math.abs(tx.amount || 0), 0);
      
      const expenseStats = {
        totalMonthlyExpenses,
        averageDailySpend,
        monthOverMonthChange,
        largestCategory,
        transactionCount: currentMonthTransactions.length,
        recurringExpenses
      };
      
      console.log('📊 Calculated expense statistics:', expenseStats);
      
      return {
        success: true,
        data: expenseStats
      };
    } catch (error) {
      console.error('❌ Error calculating expense statistics:', error);
      
      // Return fallback data
      return {
        success: false,
        error: error.message,
        data: {
          totalMonthlyExpenses: 0,
          averageDailySpend: 0,
          monthOverMonthChange: 0,
          largestCategory: {
            name: '',
            amount: 0,
            percentage: 0
          },
          transactionCount: 0,
          recurringExpenses: 0
        }
      };
    }
  }
};

export default transactionService; 